import { slugifyWithCounter } from '@sindresorhus/slugify'
import parse, { DOMNode, Element, attributesToProps, domToReact } from 'html-react-parser'
import omit from 'lodash/omit'

import { Fence } from '~/portal/components/SyntaxRenderer/components/Fence'
import { Heading } from '~/portal/components/SyntaxRenderer/components/Heading'
import { reactNodeToString } from '~/portal/utils/shared/dom'
import { CONTENT_TYPE } from '~/shared/types/content'

function getNodeText(node) {
  let text = ''
  for (const child of node.children ?? []) {
    if (typeof child === 'string') {
      text += child
    }
    text += getNodeText(child)
  }
  return text
}

function getNodeTextExcludingCode(node): string {
  let text = ''
  for (const child of node.children ?? []) {
    if (typeof child === 'string') {
      text += child
    } else if (child.name !== 'code') {
      text += getNodeTextExcludingCode(child)
    }
  }
  return text.trim()
}

export function collectHeadings({ nodes, slugify = slugifyWithCounter(), type, useMarkdocRenderer = false }) {
  const sections: any = []

  if (!Array.isArray(nodes)) {
    return sections
  }

  for (const node of nodes) {

    if (type === CONTENT_TYPE.HTML) {
      if (typeof node.type === 'function' && (node.props.level === 2 || node.props.level === 3)) {
        const title = reactNodeToString(node.props.children) // we don't care about styling or extra tags, just the text

        if (title.replace(/[\u00a0\s]/g, '')) {
          if (node.props.level === 3) {
            if (!sections[sections.length - 1]) {
              continue
            }
            sections[sections.length - 1]?.children.push({
              ...omit(node.props, 'style'),
              title,
            })
          } else {
            sections.push({ ...node.props, title, children: [] })
          }
        }
      }

      if (node.name !== 'blockquote') {
        sections.push(...collectHeadings({ nodes: node?.props?.children ?? [], slugify, type, useMarkdocRenderer }))
      }
    } else {

      if (node.name === 'Heading' && (node.attributes.level === 3 || node.attributes.level === 2)) {
        const title = getNodeText(node)
        if (title.replace(/[\u00a0\s]/g, '')) {
          const id = useMarkdocRenderer
            ? (type === CONTENT_TYPE.A2A ? getNodeTextExcludingCode(node).toLowerCase() : title.toLowerCase())
            : (node.attributes.id ?? slugify(title))
          node.attributes.id = id
          if (node.attributes.level === 3) {
            if (!sections[sections.length - 1]) {
              continue
            }
            sections[sections.length - 1]?.children.push({
              ...node.attributes,
              title,
            })
          } else {
            sections.push({ ...node.attributes, title, children: [] })
          }
        }
      }

      if (node.name !== 'blockquote') {
        sections.push(...collectHeadings({ nodes: node.children ?? [], slugify, type, useMarkdocRenderer }))
      }
    }
  }
  return sections
}

export const parseHtml = (content) => {
  if (typeof content === 'string') {
    return parse(content, {
      replace: (domNode) => {
        const { name: tagName, attribs, children } = domNode as Element
        if (/^h[1-6]$/.test(tagName) && children?.length) {
          const props = attributesToProps(attribs)
          const slugify = slugifyWithCounter()

          const getDataValue = (object) => {
            if (object?.data) {
              return object.data
            }
            if (Array.isArray(object?.children) && object?.children.length) {
              return getDataValue(object.children[0])
            }
            return ''
          }

          const id = slugify(getDataValue(children[0])) || ''
          return (
            <Heading
              {...props}
              key={id}
              id={id}
              level={parseInt(tagName[1])}
            >
              {domToReact(children as DOMNode[])}
            </Heading>
          )
        }
        if (/^pre$/.test(tagName)) {
          const codeChild: any = children?.[0] as Element && (children[0] as Element)?.name === 'code' && children?.[0]
          if (codeChild) {
            const language = codeChild?.attribs?.class && codeChild?.attribs?.class?.replace('language-', '') || 'text'
            return <Fence language={language}>{codeChild?.children?.[0]?.data as string}</Fence>
          }
        }

        if (tagName === 'a') {
          const href = attribs.href || ''
          const props = attributesToProps(attribs)

          if (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('//')) {
            return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{domToReact(children as DOMNode[])}</a>
          }
          if (href.startsWith('#')) {
            return <a href={href}
              onClick={() => {
                const element = document.getElementById(href.slice(1))
                if (element) {
                  element.scrollIntoView()
                }
              }}
              {...props}
            >
              {domToReact(children as DOMNode[])}
            </a>
          }
        }

        // If a media embed was found, convert it to an iframe element.
        // The media URL comes either from a semantic <oembed url> child or,
        // for content saved with mediaEmbed previewsInData, from the
        // data-oembed-url attribute of the preview wrapper div.
        if (/^figure$/.test(tagName) && attribs.class === 'media') {
          const firstChild = children?.[0] as Element
          const url = firstChild?.attribs?.url ?? firstChild?.attribs?.['data-oembed-url']

          const youtubeMatch = [
            /(?:m\.)?youtube\.com\/watch\?v=([\w-]+)(?:&t=(\d+))?/,
            /(?:m\.)?youtube\.com\/shorts\/([\w-]+)(?:\?t=(\d+))?/,
            /(?:m\.)?youtube\.com\/v\/([\w-]+)(?:\?t=(\d+))?/,
            /youtube\.com\/embed\/([\w-]+)(?:\?start=(\d+))?/,
            /youtu\.be\/([\w-]+)(?:\?t=(\d+))?/
          ].map(youtubeRegex => url?.match(youtubeRegex)).find(Boolean)

          if (youtubeMatch) {
            const [, id, time] = youtubeMatch
            const iframeElement =
          '<div style="position: relative; padding-bottom: 100%; height: 0; padding-bottom: 56.2493%;">' +
          `<iframe src="https://www.youtube.com/embed/${id}${time ? `?start=${time}` : ''}" ` +
          'style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;" ' +
          'frameborder="0" allow="autoplay; encrypted-media" allowfullscreen>' +
          '</iframe>' +
          '</div>'
            return <div dangerouslySetInnerHTML={{ __html: iframeElement }} />
          }

          const vimeoMatch = [
            /vimeo\.com\/(\d+)/,
            /vimeo\.com\/[^/]+\/[^/]+\/video\/(\d+)/,
            /vimeo\.com\/album\/[^/]+\/video\/(\d+)/,
            /vimeo\.com\/channels\/[^/]+\/(\d+)/,
            /vimeo\.com\/groups\/[^/]+\/videos\/(\d+)/,
            /vimeo\.com\/ondemand\/[^/]+\/(\d+)/,
            /player\.vimeo\.com\/video\/(\d+)/
          ].map(vimeoRegex => url?.match(vimeoRegex)).find(Boolean)

          if (vimeoMatch) {
            const iframeElement = '<div style="position: relative; padding-bottom: 100%; height: 0; padding-bottom: 56.2493%;">' +
          `<iframe src="https://player.vimeo.com/video/${vimeoMatch[1]}" ` +
          'style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;" ' +
          'frameborder="0" webkitallowfullscreen mozallowfullscreen allowfullscreen>' +
          '</iframe>' +
          '</div>'
            return <div dangerouslySetInnerHTML={{ __html: iframeElement }} />
          }

          return domNode
        }
      }
    }) 
  } 
  return ''
}